import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import useLogout from "../../../hooks/useLogout";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
    Block,
    BlockBetween,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Button,
    Col,
    Icon,
    PreviewAltCard,
    Row,
} from "../../../components/Component";
import { Form } from "reactstrap";
import MyInput from "../../../MyComp/UI/form/MyInput";
import ImageUpload from "../../../MyComp/UI/form/ImageUpload";
import MyButton from "../../../MyComp/UI/form/MyButton";
import { createLabBankAPI, getLabDetailsFour, getLabDetailsTwo } from "../../../api/lab";
import { toast } from "react-toastify";
import { BASE_URL_IMG } from "../../../assets/constants";
import moment from "moment";
import MyDate from "../../../MyComp/UI/form/MyDate";
import { StepHead } from "./DocFormHeader";
import { addHospitalDoctorQualification } from "../../../api/Hospital";
import { getHospitalDoctorQualification } from "../../../api/Hospital";
import { updateHospitalDoctorQualification } from "../../../api/Hospital";

const LabBank = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useLogout();
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        trigger,
        clearErrors,
        reset,
        setError,
        getValues,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {
            isLoading: false,
            agreed: false,
            medical_license_number: "",
            license_expiry_date: null,
            qualificationDD: [],
            specializationDD: [],

            objectionKeys: [],
            approvedKeys: [],

            passbook: null,
            passbook_image: null,

            hospital_certification: [
                {
                    image: null,
                    image_url: null,
                },
            ],

            mode: location?.state?.mode || "Add",
            actionType: null,
        },
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: "hospital_certification",
    });

    // Create a wrapped remove function
    const wrappedRemove = (index) => {
        const imageField = fields[index];

        // If this image has an ID (exists in database), track it for removal
        if (imageField?.id) {
            setRemovedImageIds((prev) => [...prev, imageField.id]);
        }

        // Call the original remove
        remove(index);
    };

    const [removedImageIds, setRemovedImageIds] = useState([]);
    const [mode, setMode] = useState(location?.state?.mode || "Add");
    const doctorId = location?.state?.doctorId || "";
    const queryParams = new URLSearchParams(location.search);
    const labId = queryParams.get("labId");
    // const isLoading = watch("isLoading");
    const [imageOpen, setImageOpen] = useState(false);
    const handleImageOpenPreview = (row) => {
        const imageUrl = `${row?.image}`;
        setValue("image_preview", imageUrl);
        setImageOpen(true);
    };

    const [isLoading, setIsLoading] = useState(false);

    const onFormSubmit = async (data) => {
        try {
            setIsLoading(true);

            const formData = new FormData();
            // Required Fields
            formData.append(
                "medical_license_number",
                data?.medical_license_number || ""
            );

            formData.append(
                "license_expiry_date",
                data?.license_expiry_date
                    ? moment(data.license_expiry_date).format("YYYY-MM-DD")
                    : ""
            );

            formData.append("doctorId", doctorId || "");

            data?.hospital_certification?.forEach((item, index) => {
                const file = item?.image instanceof File ? item.image : null;
                if (file) {
                    formData.append(`hospital_certification[${index}].image`, file);
                }
            });

            // Debug: inspect payload before sending
            for (let [key, value] of formData.entries()) {
                console.log(key, value);
            }

            const res = await addHospitalDoctorQualification(formData);

            if (res?.data?.status === true) {
                toast.success(res.data.message);
                navigate("/add-hospital-doctor", { state: { mode: "Edit", doctorId } });
            } else if (res?.data?.status === false) {
                toast.error(res.data.message);
            } else if (res?.data?.status === "expired") {
                toast.error(res.data.message);
                logout();
            }

        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        } finally {
            setIsLoading(false); // ✅ Always runs, even on error
        }
    };

    const handleRemoveImage = (index) => {
        const imageField = fields[index];
        console.log(imageField, "imagefiled")
        // Check if this image has an ID (exists in database)
        if (imageField?.id) {
            // Add the ID to removedImageIds state
            setRemovedImageIds((prev) => [...prev, imageField.id]);
        }

        // Remove the field from field array
        remove(index);
    };

    useEffect(() => {
        if (mode === "Edit" && doctorId) {
            getQualificationDetails();
        }
    }, [mode, doctorId]);

    const getQualificationDetails = async () => {
        try {
            setIsLoading(true);

            const res = await getHospitalDoctorQualification({
                doctorId: doctorId,
            });

            if (res?.data?.status === true) {
                const apiData = res?.data?.data;

                // ✅ Transform API → Form structure
                const formattedData = {
                    medical_license_number: apiData?.medical_license_number || "",

                    license_expiry_date: apiData?.license_expiry_date
                        ? new Date(apiData.license_expiry_date)
                        : null,

                    hospital_certification:
                        apiData?.certifications?.length > 0
                            ? apiData.certifications.map((item) => ({
                                image: `${BASE_URL_IMG}${item.url}`, // preview
                                image_url: `${BASE_URL_IMG}${item.url}`,
                            }))
                            : [{ image: null, image_url: null }],
                };

                // ✅ Reset entire form at once (BEST)
                reset({
                    ...getValues(), // keep existing values
                    ...formattedData,
                });

            } else if (res?.data?.status === "expired") {
                toast.error(res.data.message);
                logout();
            }

        } catch (error) {
            console.error(error);
            toast.error("Failed to load data");
        } finally {
            setIsLoading(false);
        }
    };

    const updateQualification = async (data) => {
        try {
            setIsLoading(true);

            const formData = new FormData();

            formData.append("id", doctorId);
            formData.append("medical_license_number", data?.medical_license_number || "");

            formData.append(
                "license_expiry_date",
                data?.license_expiry_date
                    ? moment(data.license_expiry_date).format("YYYY-MM-DD")
                    : ""
            );

            // ✅ New Images
            data?.hospital_certification?.forEach((item, index) => {
                if (item?.image instanceof File) {
                    formData.append(`hospital_certification[${index}].image`, item.image);
                }
            });

            // ✅ Removed Images
            removedImageIds.forEach((id, index) => {
                formData.append(`remove_ids[${index}]`, id);
            });

            const res = await updateHospitalDoctorQualification(formData);

            if (res?.data?.status === true) {
                toast.success(res.data.message);
                navigate("/add-hospital-doctor", {
                    state: { mode: "Edit", doctorId }
                });
            } else if (res?.data?.status === false) {
                toast.error(res.data.message);
            } else if (res?.data?.status === "expired") {
                logout();
            }

        } catch (error) {
            console.error(error);
            toast.error("Update failed");
        } finally {
            setIsLoading(false);
        }
    };

  
    return (
        <>
            <Head title={"Doctor additional details"} />
            <Content>
                <BlockHead size={"sm"}>
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle page tag="h3">
                                {"Doctor Additional Details"}
                            </BlockTitle>
                        </BlockHeadContent>
                        <BlockHeadContent>
                            <div className="">
                                <Button
                                    color="primary"
                                    outline
                                    className={`  me-n1`}
                                    onClick={() => {
                                        navigate(-1);
                                    }}
                                >
                                    <Icon name={"arrow-left"} />
                                </Button>
                            </div>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <Block>
                    <PreviewAltCard>
                        <StepHead current={2} />
                        <Form onSubmit={handleSubmit(mode === "Edit" ? updateQualification : onFormSubmit)}>
                            <Row>
                                <Col xs="12" sm="6" md="4" lg="3">
                                    <MyInput
                                        register={register}
                                        watch={watch}
                                        setValue={setValue}
                                        trigger={trigger}
                                        errors={errors}
                                        title="Medical License Number"
                                        name="medical_license_number"
                                        placeholder="Enter Medical License Number"
                                        required
                                        disabled={mode == "Approval"}
                                    />
                                </Col>
                                <Col md="4" className={``}>
                                    <MyDate
                                        register={register}
                                        watch={watch}
                                        setValue={setValue}
                                        trigger={trigger}
                                        errors={errors}
                                        title={"License Expiry Date"}
                                        name={"license_expiry_date"}
                                        placeholder={"Enter license expiry date"}
                                        required
                                        // max={moment().subtract(20, "years").toDate()}
                                        // min={moment().subtract(90, "years").toDate()}
                                        disabled={mode == "Approval"}
                                    />
                                </Col>
                                {fields.map((field, index) => (
                                    <>
                                        <Col md="2" key={field.id}>
                                            <ImageUpload
                                                getValues={getValues}
                                                id={`image_${index}`}
                                                watch={watch}
                                                trigger={trigger}
                                                setError={setError}
                                                clearErrors={clearErrors}
                                                errors={errors}
                                                register={register}
                                                label={`certification ${index + 1}`}
                                                image={`hospital_certification.${index}.image`}
                                                image_url={`hospital_certification.${index}.image_url`}
                                                setValue={setValue}
                                                handleImageOpenPreview={handleImageOpenPreview}
                                                rm
                                                index={index}
                                                remove={() => handleRemoveImage(index)} // Use custom remove handler
                                                approve={mode === "Approval" ? true : false}
                                                size={2048}
                                            />
                                        </Col>
                                    </>
                                ))}
                                {(watch("mode") === "Add" || watch("mode") === "Edit") && (
                                    <Col md="2">
                                        <Button
                                            style={{ marginTop: "30px" }}
                                            type="button"
                                            color="primary"
                                            className=" "
                                            id="addDegree"
                                            onClick={() => {
                                                append({ image: null, image_url: null });
                                            }}
                                        >
                                            {"Add Image"}
                                        </Button>{" "}
                                    </Col>
                                )}
                            </Row>
                            {(mode == "Add" || mode == "Edit") && (
                                <Row>
                                    <Col md="12" className={``}>
                                        <div className="form-group d-flex justify-content-end" style={{ marginTop: "31px" }}>
                                            <MyButton isLoading={isLoading} mode={mode} type="submit" />
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Form>
                    </PreviewAltCard>
                </Block>
            </Content>
        </>
    );
};

export default LabBank;
